/**
 * Input configuration and types.
 * Defines available input actions and whether they are continuous or single-press.
 */

export type InputType = 'continuous' | 'single' | 'modifier';

/**
 * Source of truth for all available input actions.
 * - 'continuous': held inputs (movement keys)
 * - 'single': press-once inputs (jump, interact) - reset after frame
 * - 'modifier': held inputs that modify state but don't cause movement (sneak)
 */
export const INPUTS = {
  up: 'continuous',
  down: 'continuous',
  left: 'continuous',
  right: 'continuous',
  interact: 'single',
  attack: 'single',
  sneak: 'modifier',
} as const satisfies Record<string, InputType>;

export type InputAction = keyof typeof INPUTS;

/**
 * Input state - all actions as booleans.
 */
export type InputState = Record<InputAction, boolean> & {
  /**
   * Pending corrections (in ms) to be applied this tick.
   * Positive value means "add extra movement" (undershoot).
   * Negative value means "remove movement" (overshoot) - typically rarely used for this logic.
   */
  corrections?: Partial<Record<InputAction, number>>;
};

/**
 * Derived lists for services/systems.
 */
export const CONTINUOUS_ACTIONS: InputAction[] = (
  Object.entries(INPUTS) as [InputAction, InputType][]
)
  .filter(([, type]) => type === 'continuous' || type === 'modifier')
  .map(([action]) => action);

export const SINGLE_ACTIONS: InputAction[] = (Object.entries(INPUTS) as [InputAction, InputType][])
  .filter(([, type]) => type === 'single')
  .map(([action]) => action);

/**
 * Default input state with all actions set to false.
 */
export const DEFAULT_INPUT_STATE: InputState = Object.fromEntries(
  Object.keys(INPUTS).map((k) => [k, false]),
) as unknown as InputState;

/**
 * Check if an action is continuous (held) or single (press-once).
 * Modifiers are treated as continuous for networking purposes (start/end events).
 */
export function isContinuous(action: InputAction): boolean {
  return INPUTS[action] === 'continuous' || INPUTS[action] === 'modifier';
}

/**
 * Check if movement-related inputs have changed between two input states.
 * Useful to avoid unnecessary component updates when input hasn't changed.
 */
export function inputMovementChanged(a: InputState, b: InputState): boolean {
  return (
    a.up !== b.up ||
    a.down !== b.down ||
    a.left !== b.left ||
    a.right !== b.right ||
    a.sneak !== b.sneak
  );
}

/** Movement actions that should be blocked when UI is open.
 *  Excludes 'modifier' types like sneak.
 */
export const MOVEMENT_ACTIONS: readonly InputAction[] = (
  Object.entries(INPUTS) as [InputAction, InputType][]
)
  .filter(([, type]) => type === 'continuous')
  .map(([action]) => action);

/** Check if an action is a movement action (causes displacement) */
export function isMovementAction(action: InputAction): boolean {
  return MOVEMENT_ACTIONS.includes(action);
}
